import React, { useState, useMemo } from 'react'

import { Check, ChevronsUpDown } from 'lucide-react'
import { useFormContext, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AlegraLocationOption } from '@/core/hooks/payment/useAlegraLocations'
import { Button } from '@/shared/components/atoms/Button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/atoms/Command'
import { Label } from '@/shared/components/atoms/Label/Label'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/atoms/Popover'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import { cn } from '@/shared/utils/helpers'

import { BillingInformationFormData } from '../BillingInformation.types'

export interface AlegraColAddressFieldsProps {
	departments: AlegraLocationOption[]
	cities: AlegraLocationOption[]
	zipCodes: AlegraLocationOption[]
	isLoadingCities: boolean
	isLoadingZipCodes: boolean
	selectedDepartmentId: number | undefined
	selectedCity: string
}

export const AlegraColAddressFields: React.FC<AlegraColAddressFieldsProps> = ({
	departments,
	cities,
	zipCodes,
	isLoadingCities,
	isLoadingZipCodes,
	selectedDepartmentId,
	selectedCity,
}) => {
	const { t } = useTranslation('common')
	const {
		control,
		setValue,
		formState: { errors },
	} = useFormContext<BillingInformationFormData>()
	const [zipSearchOpen, setZipSearchOpen] = useState(false)
	const [zipSearchQuery, setZipSearchQuery] = useState('')

	// Filter ZIP codes based on search query for performance
	const filteredZipCodes = useMemo(() => {
		if (!zipSearchQuery) return zipCodes.slice(0, 100) // Show first 100 by default
		const query = zipSearchQuery.toLowerCase()
		return zipCodes
			.filter((zip) => zip.value.toLowerCase().includes(query))
			.slice(0, 100)
	}, [zipCodes, zipSearchQuery])

	return (
		<>
			{/* State/Department */}
			<div className="flex flex-col gap-2">
				<Label htmlFor="state">
					{t('account.billing_information.state')} *
				</Label>
				<Controller
					name="state"
					control={control}
					render={({ field }) => (
						<Select
							value={field.value || ''}
							onValueChange={(value) => {
								field.onChange(value)
								setValue('city', '')
								setValue('zip', '')
							}}
						>
							<SelectTrigger
								className="bg-input w-full"
								aria-invalid={!!errors.state}
							>
								<SelectValue
									placeholder={t(
										'account.billing_information.select_department'
									)}
								/>
							</SelectTrigger>
							<SelectContent>
								{departments.map((department) => (
									<SelectItem key={department.id} value={String(department.id)}>
										{department.value}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					)}
				/>
				{errors.state && (
					<p className="text-sm text-destructive">{errors.state.message}</p>
				)}
			</div>

			{/* City */}
			<div className="flex flex-col gap-2">
				<Label htmlFor="city">{t('account.billing_information.city')} *</Label>
				{isLoadingCities ? (
					<Skeleton className="h-10 w-full" />
				) : (
					<Controller
						name="city"
						control={control}
						render={({ field }) => (
							<Select
								value={field.value || ''}
								onValueChange={(value) => {
									field.onChange(value)
									setValue('zip', '')
								}}
								disabled={!selectedDepartmentId || isLoadingCities}
							>
								<SelectTrigger
									className="bg-input w-full"
									aria-invalid={!!errors.city}
								>
									<SelectValue
										placeholder={t('account.billing_information.select_city')}
									/>
								</SelectTrigger>
								<SelectContent>
									{cities.map((city) => (
										<SelectItem key={city.id} value={city.value}>
											{city.value}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
					/>
				)}
				{errors.city && (
					<p className="text-sm text-destructive">{errors.city.message}</p>
				)}
			</div>

			{/* ZIP Code - Searchable combobox for Colombia (handles large datasets) */}
			<div className="flex flex-col gap-2">
				<Label htmlFor="zip">{t('account.billing_information.zip')} *</Label>
				{isLoadingZipCodes ? (
					<Skeleton className="h-10 w-full" />
				) : (
					<Controller
						name="zip"
						control={control}
						render={({ field }) => (
							<Popover open={zipSearchOpen} onOpenChange={setZipSearchOpen}>
								<PopoverTrigger asChild>
									<Button
										variant="outline"
										role="combobox"
										aria-expanded={zipSearchOpen}
										disabled={!selectedCity || isLoadingZipCodes}
										className={cn(
											'w-full justify-between bg-input font-normal',
											errors.zip && 'border-destructive'
										)}
									>
										{field.value || t('account.billing_information.select_zip')}
										<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
									</Button>
								</PopoverTrigger>
								<PopoverContent
									className="w-[--radix-popover-trigger-width] p-0"
									align="start"
								>
									<Command shouldFilter={false}>
										<CommandInput
											placeholder={t('account.billing_information.search_zip')}
											value={zipSearchQuery}
											onValueChange={setZipSearchQuery}
										/>
										<CommandList>
											<CommandEmpty>
												{t('account.billing_information.no_zip_found')}
											</CommandEmpty>
											<CommandGroup>
												{filteredZipCodes.map((zip) => (
													<CommandItem
														key={zip.id}
														value={zip.value}
														onSelect={(value) => {
															field.onChange(value)
															setZipSearchOpen(false)
															setZipSearchQuery('')
														}}
													>
														<Check
															className={cn(
																'mr-2 h-4 w-4',
																field.value === zip.value
																	? 'opacity-100'
																	: 'opacity-0'
															)}
														/>
														{zip.value}
													</CommandItem>
												))}
											</CommandGroup>
										</CommandList>
									</Command>
								</PopoverContent>
							</Popover>
						)}
					/>
				)}
				{errors.zip && (
					<p className="text-sm text-destructive">{errors.zip.message}</p>
				)}
			</div>
		</>
	)
}
